import { ButtonType, ButtonWidget } from '@tableau/widgets';
import React from 'react';

import { Messages } from '../../../codegen/Localize';
import { WelcomeChannelStateProps } from '../../../home/components/WelcomeChannel';
import { WelcomeChannelProps } from '../../../home/utils/WelcomeChannelPropsByRole';
import { SiteRoles } from '../../../userManagement/users/serverApi/types/ISiteRole';
import { SettingsWelcomeChannelStyles } from '../styles/SettingsWelcomeChannelStyles';
import { CharacterCounter } from './CharacterCounter';
import { Preview } from './Preview';
import { LazyTinyEditor } from './tinyEditor/LazyTinyEditor';
import { TinyEditorChangeHandler } from './tinyEditor/TinyEditor';
import { TinyEditorConfigs } from './tinyEditor/TinyEditorConfig';
import { WelcomeChannelCloseButton } from './WelcomeChannelCloseButton';
import { WelcomeChannelPreview } from './WelcomeChannelPreview';

export interface FooterEditorProps {
  readonly formName: string;
  readonly value: string;
  readonly showDefaultWelcomeChannel: boolean;
  readonly onChange: (value: string, isValid: boolean) => void;
}

interface FooterEditorState {
  showPreview: boolean;
  characterCount: number;
  newLineCount: number;
}

const testIds = {
  previewButton: 'footerEditorPreview'
};

const CHARACTER_LIMIT = 240;
const NEW_LINE_LIMIT = 2;

export class FooterEditor extends React.PureComponent<FooterEditorProps, FooterEditorState> {
  private welcomeChannelProps: WelcomeChannelStateProps;

  constructor(props: FooterEditorProps) {
    super(props);
    this.state = {
      showPreview: false,
      characterCount: 0,
      newLineCount: 0
    };
    this.welcomeChannelProps = WelcomeChannelProps.getPropsByRole(SiteRoles.ExplorerCanPublish, true);
  }

  render() {
    const toggleButtonText = this.state.showPreview ? Messages.settings_welcomeChannel_footerEditor_edit()
      : Messages.settings_welcomeChannel_footerEditor_preview();

    const closeButton =
      <WelcomeChannelCloseButton
        handleClick={() => {}}
        tooltipText={Messages.settings_welcomeChannel_dismiss_tooltip()}
        disabled={true}
        footerOnly={true}
      />;

    const editor =
      <React.Fragment>
        {this.props.showDefaultWelcomeChannel &&
          <div style={SettingsWelcomeChannelStyles.WelcomeChannelPreviewContainer}>
            <WelcomeChannelPreview {...this.welcomeChannelProps} />
          </div>
        }
        <div className={SettingsWelcomeChannelStyles.FooterContainer}>
          <LazyTinyEditor
            id={this.props.formName}
            formName={this.props.formName}
            editorConfig={TinyEditorConfigs.welcomeChannelEditor('#' + this.props.formName, this.updateCharacterCount)}
            value={this.props.value}
            changeHandler={this.handleEditorChange}
          />
          {!this.props.showDefaultWelcomeChannel && closeButton}
        </div>
      </React.Fragment>;

    const toggleButton =
      <ButtonWidget
        buttonType={ButtonType.Outline}
        handleClick={this.handlePreviewToggle}
        testId={testIds.previewButton}
      >
        {toggleButtonText}
      </ButtonWidget>;

    return (
      <>
        <p>{Messages.settings_welcomeChannel_serverEditor_description()}</p>
        <section style={SettingsWelcomeChannelStyles.FooterPreviewContainerStyle}>
          {this.state.showPreview
            ? <Preview showDefaultWelcomeChannel={this.props.showDefaultWelcomeChannel} html={this.props.value} welcomeChannelProps={this.welcomeChannelProps}/>
            : editor
          }
        </section>
        <CharacterCounter
          characterCount={this.state.characterCount}
          characterLimit={CHARACTER_LIMIT}
          newLineCount={this.state.newLineCount}
          newLineLimit={NEW_LINE_LIMIT}
        />
        {toggleButton}
      </>
    );
  }

  private handlePreviewToggle = () => {
    this.setState((previousState) => ({
      showPreview: !previousState.showPreview
    }));
  };

  private handleEditorChange: TinyEditorChangeHandler = (value: string) => {
    this.props.onChange(value, this.isFooterValid());
  };

  private isFooterValid(){
    return this.state.characterCount <= CHARACTER_LIMIT && this.state.newLineCount <= NEW_LINE_LIMIT;
  }

  private updateCharacterCount = (characterCount: number, newLineCount: number) => {
    this.setState({
      characterCount,
      newLineCount
    });
  };
}
